import { Box, Grid, Text, useRadio, useRadioGroup, type UseRadioProps, VisuallyHidden } from "@chakra-ui/react";
import { useState, type ReactNode } from "react";
import { CtaButton, Reveal, RichText } from "@/components";
import {
  ANNUAL_SAVE_CHIP,
  BILLING_LEGEND,
  BILLING_OPTIONS,
  PLANS,
  SEAT_BILLED_ANNUAL_NOTE,
  SEAT_BILLED_MONTHLY_NOTE,
  SEAT_PRICE_UNIT,
  seatPrice,
  usd,
  type BillingPeriod,
  type Plan,
} from "@/content";
import { CARD_TONES, type CardTone } from "@/theme/foundations/cardTones";
import { focusRing } from "@/theme/foundations/focus";

type BillingOptionProps = UseRadioProps & { label: string; children: ReactNode };

const BillingOption = ({ label, children, ...radioProps }: BillingOptionProps) => {
  const { state, getInputProps, getRadioProps } = useRadio(radioProps);

  return (
    <Box as="label" cursor="pointer">
      <input {...getInputProps()} aria-label={label} />
      <Box
        {...getRadioProps()}
        display="inline-flex"
        alignItems="center"
        gap="10px"
        minH="44px"
        px="22px"
        py="12px"
        borderRadius="sm"
        fontSize="clamp(0.875rem, 1.1vw, 1rem)"
        letterSpacing="0.003em"
        textTransform="uppercase"
        whiteSpace="nowrap"
        color={state.isChecked ? "ink.light" : "warm.600"}
        bg={state.isChecked ? "ink.DEFAULT" : "transparent"}
        transitionProperty="background-color, color"
        transitionDuration="0.25s"
        transitionTimingFunction="brand"
        _hover={state.isChecked ? undefined : { color: "ink.DEFAULT" }}
        _focusVisible={focusRing}
      >
        {children}
      </Box>
    </Box>
  );
};

const BillingSwitch = ({ value, onChange }: { value: BillingPeriod; onChange: (next: BillingPeriod) => void }) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "billing",
    value,
    onChange: (next) => onChange(next as BillingPeriod),
  });

  return (
    <Box as="fieldset" className="print-hide" display="grid" justifyItems="center" border={0} p={0}>
      <VisuallyHidden as="legend">{BILLING_LEGEND}</VisuallyHidden>
      <Box
        {...getRootProps()}
        display="inline-flex"
        gap="4px"
        p="4px"
        mb="clamp(32px, 4vw, 48px)"
        borderRadius="md"
        bg="surface.white"
        border="1px solid"
        borderColor="line.hairline"
      >
        {BILLING_OPTIONS.map((option) => (
          <BillingOption key={option.value} label={option.label} {...getRadioProps({ value: option.value })}>
            {option.label}
            {option.value === "annual" && (
              <Box
                as="span"
                fontSize="0.78em"
                letterSpacing="0.1em"
                color={value === "annual" ? "ink.light" : "brand.600"}
              >
                {ANNUAL_SAVE_CHIP}
              </Box>
            )}
          </BillingOption>
        ))}
      </Box>
    </Box>
  );
};

const SeatRate = ({
  monthlyUsd,
  period,
  isActive,
  tone,
}: {
  monthlyUsd: number;
  period: BillingPeriod;
  isActive: boolean;
  tone: CardTone;
}) => (
  <>
    <Text
      className="plan-rate"
      display={isActive ? "block" : "none"}
      fontSize="clamp(2rem, 3.4vw, 3.25rem)"
      fontWeight="light"
      lineHeight={0.85}
      sx={{ textWrap: "balance" }}
    >
      {seatPrice(monthlyUsd)[period]}{" "}
      <Box as="span" fontSize="0.3em" color={tone.muted}>
        {SEAT_PRICE_UNIT}
      </Box>
    </Text>
    <Text
      className="plan-rate"
      display={isActive ? "block" : "none"}
      fontSize="small"
      lineHeight={1.35}
      color={tone.accent}
      mt="-10px"
      sx={{ textWrap: "pretty" }}
    >
      {period === "annual" ? (
        <>
          <Box as="s">{usd(monthlyUsd)}</Box> {SEAT_BILLED_ANNUAL_NOTE.rest}
        </>
      ) : (
        SEAT_BILLED_MONTHLY_NOTE
      )}
    </Text>
  </>
);

const PlanCard = ({ plan, period, tone }: { plan: Plan; period: BillingPeriod; tone: CardTone }) => (
  <Reveal
    className={tone.isDark ? "reveal print-avoid-break print-flat" : "reveal print-avoid-break"}
    display="flex"
    flexDirection="column"
    gap="18px"
    h="100%"
    p="clamp(28px, 3.4vw, 44px)"
    borderRadius="lg"
    bg={tone.bg}
    color={tone.isDark ? "ink.light" : "ink.DEFAULT"}
    border="1px solid"
    borderColor={tone.isDark ? "line.glassBorder" : "line.hairline"}
    transitionProperty="border-color"
    transitionDuration="0.25s"
    transitionTimingFunction="brand"
    _hover={{ borderColor: "brand.500" }}
    as="article"
  >
    <Text as="h2" variant="eyebrow" color={plan.highlighted ? tone.accent : tone.muted}>
      <RichText nodes={plan.name} />
    </Text>

    {plan.price.kind === "seat" ? (
      <>
        <SeatRate monthlyUsd={plan.price.monthlyUsd} period="monthly" isActive={period === "monthly"} tone={tone} />
        <SeatRate monthlyUsd={plan.price.monthlyUsd} period="annual" isActive={period === "annual"} tone={tone} />
      </>
    ) : (
      <>
        <Text fontSize="clamp(2rem, 3.4vw, 3.25rem)" fontWeight="light" lineHeight={0.85} sx={{ textWrap: "balance" }}>
          {plan.price.label}
        </Text>
        <Text fontSize="small" lineHeight={1.35} color={tone.accent} mt="-10px" sx={{ textWrap: "pretty" }}>
          {plan.price.billed}
        </Text>
      </>
    )}

    <Text
      fontSize="small"
      lineHeight={1.35}
      color={tone.muted}
      pt="16px"
      borderTop="1px solid"
      borderColor={tone.isDark ? "line.glassBorder" : "line.hairline"}
    >
      {plan.note}
    </Text>

    <Grid as="ul" role="list" gap="12px" my="2px">
      {plan.features.map((feature) => (
        <Grid
          as="li"
          key={feature.label}
          templateColumns="auto 1fr"
          gap="12px"
          fontSize="small"
          lineHeight={1.35}
          color={feature.included ? tone.included : tone.excluded}
          pt={feature.included ? 0 : "14px"}
          borderTop={feature.included ? undefined : "1px solid"}
          borderColor={feature.included ? undefined : tone.isDark ? "line.glassBorder" : "line.hairline"}
        >
          <Box
            aria-hidden="true"
            w="6px"
            h="6px"
            borderRadius="50%"
            mt="0.5em"
            bg={feature.included ? "brand.500" : "transparent"}
            border={feature.included ? undefined : "1px solid"}
            borderColor={feature.included ? undefined : tone.excludedDot}
          />
          {feature.label}
        </Grid>
      ))}
    </Grid>

    <CtaButton
      href={plan.cta.href}
      external={plan.cta.external}
      variant={plan.cta.variant}
      w="100%"
      mt="auto"
      px="16px"
      whiteSpace="normal"
      _hover={tone.isDark ? { bg: "surface.white", color: "ink.DEFAULT" } : undefined}
    >
      {plan.cta.label}
    </CtaButton>
  </Reveal>
);

export const Plans = () => {
  const [period, setPeriod] = useState<BillingPeriod>("monthly");

  return (
    <Box as="section" id="plans" maxW="maxw" mx="auto" px="pad" pb="sec">
      <BillingSwitch value={period} onChange={setPeriod} />

      <Grid
        gap="gap"
        maxW="1180px"
        mx="auto"
        alignItems="stretch"
        autoRows={{ base: "auto", md: "1fr" }}
        templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
      >
        {PLANS.map((plan, index) => (
          <PlanCard key={plan.id} plan={plan} period={period} tone={CARD_TONES[index]} />
        ))}
      </Grid>
    </Box>
  );
};
